import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, VariantCustomUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue, isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets — The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  // CUSTOM: Tooltip functionality for swatches
  /** @type {HTMLElement | null} */
  #tooltip = null;

  /** @type {Number | null} */
  #swatchHoverTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    // CUSTOM: Initialize tooltip for swatches
    this.#tooltip = document.querySelector('.tooltip-swatch');
    if (!this.#tooltip) {
      VariantPicker.createTooltipElement();
      this.#tooltip = document.querySelector('.tooltip-swatch');
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const isQuickAddDialog = !!event.target.closest('quick-add-dialog');

    // CUSTOM: Check if this is an image picker
    const isImagePicker = event.target.hasAttribute('data-option-values-id');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    // CUSTOM: Modified morphElementSelector logic to handle image picker
    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isImagePicker
        ? 'main'
        : isOnFeaturedProductSection
          ? 'featured-product-information'
          : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, isQuickAddDialog);

    // CUSTOM: Dispatch additional variant:change event
    document.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: {
          id: event.target.dataset.variantId,
          trigger: event.target.closest('.shopify-section'),
        },
      })
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // CUSTOM: Handle image picker with data-option-values-id
    if (selectedOption.hasAttribute('data-option-values-id')) {
      params.push(`option_values=${selectedOption.dataset.optionValuesId}`);
      if (selectedOption.hasAttribute('data-section-id')) {
        params.push(`section_id=${selectedOption.dataset.sectionId}`);
      }
      return `${productUrl}?${params.join('&')}`;
    }

    // CUSTOM: Handle render API mode
    const useRenderApi =
      this.hasAttribute('data-api') && this.getAttribute('data-api') === 'true' && this.hasAttribute('data-section-id');
    if (useRenderApi) {
      const {
        dataset: { api, sectionId },
      } = this;

      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
      params.push(`section_id=${sectionId}`);
      return `${productUrl}?${params.join('&')}`;
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [isQuickAddDialog] - Is this a quick add event?.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, isQuickAddDialog = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // CUSTOM: Enhanced selector logic with section ID support
        const baseSelector = 'variant-picker';
        const sectionId = this.dataset.sectionId;
        const currentPickerSelector = sectionId
          ? `${baseSelector}[data-section-id="${sectionId}"] script[type="application/json"]`
          : `${baseSelector} script[type="application/json"]`;

        const currentPicker = html.querySelector(baseSelector);
        const textContent = html.querySelector(currentPickerSelector)?.textContent;

        if (!textContent) return;

        // CUSTOM: Check for API mode and update accordingly
        if (morphElementSelector === 'main') {
          this.updateMain(html, !!sectionId, sectionId, isQuickAddDialog);
        } else if (
          currentPicker &&
          currentPicker.hasAttribute('data-api') &&
          currentPicker.getAttribute('data-api') === 'true'
        ) {
          this.updateMain(html, !!sectionId, sectionId, isQuickAddDialog);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        this.dispatchEvent(
          new VariantCustomUpdateEvent(JSON.parse(textContent),{
            html,
            productId: this.dataset.productId ?? ''
          })
        )
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    // CUSTOM: Enhanced selector with section ID support
    const idSection = this.dataset.sectionId;
    const selector = idSection
      ? `${this.tagName.toLowerCase()}[data-section-id="${idSection}"]`
      : this.tagName.toLowerCase();

    const newVariantPickerSource = newHtml.querySelector(selector) || newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * CUSTOM: Enhanced updateMain with section-specific rendering support
   * Re-renders the entire main content or a specific section.
   * @param {Document} newHtml - The new HTML.
   * @param {boolean} [onlySection] - Render only section
   * @param {string | null} [sectionId] - Id section
   * @param {boolean} [isQuickAddDialog] - Is this a quick add event?.
   */
  updateMain(newHtml, onlySection = false, sectionId, isQuickAddDialog = false) {
    let main = null;
    let newMain = null;

    main = document.querySelector('main');
    newMain = newHtml.querySelector('main');

    if (onlySection && sectionId) {
      const sectionIdSelector = `shopify-section-${sectionId}`;
      const productInformationIdSelector = `ProductInformation-${sectionId}`;

      main =
        isQuickAddDialog == false
          ? document.querySelector(`main #${sectionIdSelector}`)
          : document.querySelector(`#quick-add-modal-content:has(#${productInformationIdSelector})`);

      newMain =
        isQuickAddDialog == false
          ? newHtml.querySelector(`#${sectionIdSelector}`)
          : newHtml.querySelector(`#${sectionIdSelector} [data-product-grid-content]`);

      if (isQuickAddDialog == true && isMobileBreakpoint() && newMain instanceof HTMLElement) {
        const {
          dataset: { productTitle: title, productUrl },
        } = this;
        main = document.querySelector(`#quick-add-modal-content`);
        const productGrid = document.createElement('div');

        const productMedia = newMain.querySelector('.product-information__media');
        const productFormComponent = newMain.querySelector('product-form-component');
        const variantPicker = newMain.querySelector('variant-picker');
        const productPrice = newMain.querySelector('product-price');
        const variantTitle = newMain.querySelector(".view-variant-title");

        if (productMedia) productGrid.appendChild(productMedia);
        if (title && productUrl) {
          const productTitle = document.createElement('a');
          productTitle.textContent = title;
          productTitle.href = productUrl;

          const productHeader = document.createElement('div');
          productHeader.classList.add('product-header');
          productHeader.appendChild(productTitle);
          if (variantTitle) productHeader.appendChild(variantTitle);
          if (productPrice) productHeader.appendChild(productPrice);
          if (productHeader) productGrid.appendChild(productHeader);
        }
        if (variantPicker) productGrid.appendChild(variantPicker);
        if (productFormComponent) productGrid.appendChild(productFormComponent);

        newMain = productGrid;
      }

      // CUSTOM: Remove hidden style element if present
      const hiddenStyle = newMain?.querySelector('#style-hidden');
      if (hiddenStyle) {
        hiddenStyle.remove();
      }
    }

    const variantSyncer = main?.querySelector('b2b-variant-syncer')
    const newVariantSyncer = newMain?.querySelector('b2b-variant-syncer')

    if(variantSyncer && newVariantSyncer) variantSyncer.outerHTML = newVariantSyncer.outerHTML


    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);

    if (!isQuickAddDialog) {
      // CUSTOM: Dispatch theme updated event
      document.dispatchEvent(
        new CustomEvent('variant:theme:updated', {
          detail: {
            sectionId: sectionId ? `shopify-section-${sectionId}` : null,
          },
        }),
      );
    } else {
      main.dispatchEvent(new CustomEvent('variant:quick:updated', { detail: null }));
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the current selected variant object.
   * @returns {Record<string, any> | undefined} The current variant object.
   */
  getCurrentVariant() {
    const variantDataScript = this.querySelector('script[type="application/json"]');

    if (!variantDataScript?.textContent) return undefined;

    try {
      return JSON.parse(variantDataScript?.textContent);
    } catch (error) {
      console.error('Unable to parse product data for current variant', error);
      return undefined;
    }
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  // CUSTOM: Tooltip functionality for swatches
  /**
   * @param {string} index
   */
  handlePointerEnter(index) {
    const { listSwatchItem } = this.refs;
    if (!Array.isArray(listSwatchItem)) return;

    if (this.#swatchHoverTimeout) clearTimeout(this.#swatchHoverTimeout);

    this.#swatchHoverTimeout = setTimeout(() => {
      const currentSwatch = listSwatchItem[Number(index)];

      if (currentSwatch && currentSwatch instanceof HTMLElement) {
        const {
          dataset: { tooltip },
        } = currentSwatch;

        if (tooltip && currentSwatch) {
          this.#undateTooltipElement(tooltip, currentSwatch);
        }
      }
    }, 500);
  }

  handlePointerLeave() {
    if (this.#swatchHoverTimeout) clearTimeout(this.#swatchHoverTimeout);
    if (this.#tooltip) this.#tooltip.classList.remove('show');
  }

  static createTooltipElement() {
    const tooltip = document.createElement('div');
    tooltip.classList.add('tooltip-swatch');

    const tooltipSpan = document.createElement('span');
    tooltip.appendChild(tooltipSpan);

    const arrow = document.createElement('div');
    arrow.classList.add('tooltip-swatch__arrow');

    tooltip.appendChild(arrow);
    document.body.appendChild(tooltip);
  }

  /**
   * @param {string} text
   * @param {HTMLElement} swatch
   */
  #undateTooltipElement(text, swatch) {
    /** @type {HTMLElement | null} */
    const tooltip = this.#tooltip;
    /** @type {HTMLElement | null | undefined} */
    const tooltipText = tooltip?.querySelector('span');
    /** @type {HTMLElement | null | undefined} */
    const arrow = tooltip?.querySelector('.tooltip-swatch__arrow');

    if (!tooltipText || !tooltip || !arrow) return;

    tooltipText.textContent = '';
    tooltipText.textContent = text;

    tooltip.classList.add('show');

    const rect = swatch.getBoundingClientRect();
    const ttRect = tooltip.getBoundingClientRect();

    let x = rect.left + rect.width / 2 - ttRect.width / 2 + window.scrollX;
    let y = rect.top - rect.height / 2 + window.scrollY;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (x < window.scrollX + 5) {
      x = window.scrollX + 5;
    } else if (x + ttRect.width > window.scrollX + viewportWidth - 5) {
      x = window.scrollX + viewportWidth - ttRect.width - 5;
    }
    if (y < window.scrollY + 5) {
      y = window.scrollY + 5;
    } else if (y + ttRect.height > window.scrollY + viewportHeight - 5) {
      y = window.scrollY + viewportHeight - ttRect.height - 5;
    }

    tooltip.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0px)`;

    const swatchCenterX = rect.left + rect.width / 2 + window.scrollX;
    const tooltipLeft = x;

    let arrowX = swatchCenterX - tooltipLeft;

    const arrowMargin = 10;
    arrowX = Math.min(Math.max(arrowX, arrowMargin), ttRect.width - arrowMargin);

    arrow.style.left = `${arrowX}px`;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
